import React, { forwardRef } from 'react';

type TermsContentProps = {
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  className?: string;
  style?: React.CSSProperties;
};

const baseStyle: React.CSSProperties = {
  textAlign: 'left',
  lineHeight: 1.55,
  color: '#374151',
  fontSize: 14
};

const TermsContent = forwardRef<HTMLDivElement, TermsContentProps>(({ onScroll, className, style }, ref) => {
  const mergedStyle = style ? { ...baseStyle, ...style } : baseStyle;

  return (
    <div ref={ref} onScroll={onScroll} className={className} style={mergedStyle}>
      <p><strong>Welcome to LoConvo.</strong> By creating an account or signing in (including with Google or Microsoft), you agree to these Terms, our Acceptable Use Policy, and Privacy Policy. If you do not agree, do not use the service.</p>

      <h4>1. Eligibility &amp; Account</h4>
      <ul>
        <li>You must be at least 13 years old (or older if your jurisdiction requires).</li>
        <li>Provide accurate information and do not impersonate others.</li>
        <li>Usernames are public and largely immutable once set; choose responsibly.</li>
      </ul>

      <h4>2. Community Standards (Strict)</h4>
      <ul>
        <li>No hate speech, harassment, threats, or incitement, including targeting people by protected characteristics (e.g., race, ethnicity, nationality, religion, gender, gender identity, sexual orientation, disability).</li>
        <li>No doxxing, stalking, or hyper-targeting private individuals (e.g., posting identifying details to facilitate harassment).</li>
        <li>No illegal activities or depictions of unlawful violence.</li>
        <li>No sexually explicit, exploitative, or otherwise unsuitable content for minors.</li>
        <li>No spam, scams, deceptive behavior, malware, or abusive automation.</li>
        <li>No infringement of intellectual property or privacy rights.</li>
      </ul>

      <h4>3. Location &amp; Safety</h4>
      <ul>
        <li>Do not use LoConvo to coordinate harm or to target private individuals.</li>
        <li>Do not share precise private addresses without consent; avoid marking sensitive or secure locations without authorization.</li>
        <li>User-submitted maps/locations may be inaccurate or stale. Verify independently.</li>
        <li>LoConvo is not an emergency service; call local authorities in emergencies and do not rely on it for safety-critical decisions.</li>
        <li>You assume all risk when visiting locations or meeting others; take appropriate personal safety precautions.</li>
      </ul>

      <h4>4. Prohibited Uses</h4>
      <ul>
        <li>No scraping at scale, reverse engineering, or overloading the service.</li>
        <li>Do not automate actions to manipulate votes, comments, or visibility.</li>
        <li>No coordinated inauthentic behavior, targeted harassment campaigns, or political persuasion at scale.</li>
      </ul>

      <h4>5. Your Content &amp; License</h4>
      <ul>
        <li>You retain rights to your content.</li>
        <li>By posting, you grant us a non-exclusive license to host, process, and display it to operate the service.</li>
        <li>Only post content you have the right to share.</li>
      </ul>

      <h4>6. Privacy &amp; Data</h4>
      <ul>
        <li>We process account data and content to operate the service; review our Privacy Policy for details.</li>
        <li>Public posts, usernames, and timestamps may be visible to others.</li>
        <li>On account deletion, we anonymize content in conversations and may retain minimal identifiers (e.g., email/auth ID) in a restricted record for legal compliance; these are not shown in the product.</li>
        <li>Analytics and ads (e.g., Google Analytics/AdSense) may require your consent in some regions; you can opt out of personalized ads and withdraw consent at any time.</li>
      </ul>

      <h4>7. Moderation &amp; Enforcement</h4>
      <ul>
        <li>We may remove content, restrict features, or ban accounts at our discretion for violations or legal risk.</li>
        <li>Severe or repeat violations can lead to immediate permanent bans.</li>
        <li>We may preserve data as required by law or to investigate abuse.</li>
      </ul>

      <h4>8. Risk, Disclaimer, &amp; Liability</h4>
      <ul>
        <li>The service is provided “as is.” User content may be inaccurate, incomplete, or harmful.</li>
        <li>We do not vet users, venues, or information before it appears in the service.</li>
        <li>Do not rely on LoConvo for navigation, safety decisions, or emergencies—contact local authorities instead.</li>
        <li>You assume all risk when acting on information from the service or interacting with others.</li>
        <li>We are not liable for indirect, consequential, incidental, exemplary, or punitive damages, or for personal injury, property damage, loss of data, business, profits, goodwill, or reputation, to the extent permitted by law.</li>
        <li>You release and hold harmless LoConvo, its affiliates, and personnel from claims related to user content, interactions, or real-world encounters initiated via the service.</li>
        <li>You agree to indemnify us for claims arising from your content, your use of the service, or your violation of these terms or others’ rights.</li>
      </ul>

      <h4>9. Changes</h4>
      <ul>
        <li>We may update these Terms. Continued use after changes signifies acceptance.</li>
      </ul>

      <p className="modal-hint">Scroll to the bottom to enable the Agree button.</p>
    </div>
  );
});

TermsContent.displayName = 'TermsContent';

export default TermsContent;
